/**
 * i18n resources for the `reception` dashboard landing (master prompt §6/§7).
 *
 * Namespace: `reception`. `bg`, `de`, `fr` mirror `en` for now (translation
 * follow-up). The coordinator merges this namespace into the platform i18n
 * aggregator. Tone: a calm hotel "operations desk", never noisy SaaS chrome.
 */

const en = {
  eyebrow: 'Reception',
  title: 'Reception desk',
  description: 'A calm overview of how your AI receptionist is handling the front desk today.',
  greeting: 'Good evening, {{hotel}}.',
  factualLine: '{{date}} · {{city}}',
  actions: {
    thisWeek: 'This week',
    newRule: '+ New rule',
    newRuleMenu: {
      callHandling: 'Call handling',
      callHandlingDescription: 'Transfer & escalation settings',
      serviceRouting: 'Service routing',
      serviceRoutingDescription: 'Send requests to a team',
    },
  },
  chart: {
    title: 'Call volume — last 14 days',
    inbound: 'Inbound',
    bookings: 'Bookings',
    transferred: 'Transferred',
  },
  recent: {
    title: 'Recent calls',
    viewAll: 'View all',
    /** Honest caller label — the backend carries no guest name/phone yet. */
    caller: 'Unknown caller',
    status: {
      booking: 'Booking',
      enquiry: 'Enquiry',
      transferred: 'Transferred',
    },
    empty: {
      title: 'Nothing to report',
      description: 'Recent calls will appear here as they come in.',
    },
  },
  live: {
    speaker: {
      guest: 'Guest',
      hostesa: 'Hostesa',
    },
    live: 'Live',
    line: 'Line',
    whisper: 'Whisper',
    takeOver: 'Take over',
    empty: 'No live calls right now',
    callerHeading: 'Guest on the line',
    direction: {
      inbound: 'Inbound',
      outbound: 'Outbound',
    },
    states: {
      loading: 'Checking the line',
      error: {
        title: 'Could not load the live call',
        description: 'Something interrupted the live call. Please try again.',
      },
    },
  },
  draft: {
    title: 'Draft reservation',
    status: 'Pending · AI',
    fields: {
      guest: 'Guest',
      dates: 'Dates',
      room: 'Room',
      rate: 'Rate',
    },
    confirm: 'Confirm',
    hold: 'Hold',
  },
  states: {
    error: {
      title: 'Could not load the desk',
      description: 'Something interrupted the reception desk. Please try again.',
    },
    loading: 'Loading the reception desk',
    forbidden: {
      title: 'No access to this desk',
      description: 'Ask an administrator for reception access.',
    },
  },
  metrics: {
    callsHandled: {
      label: 'Calls handled',
      help: 'Calls answered in the current view.',
    },
    bookingsCaptured: {
      label: 'Bookings captured',
      help: 'Calls that turned into a completed conversation.',
    },
    avgHandlingTime: {
      label: 'Average handling time',
      help: 'Mean duration across completed calls.',
    },
    languagesServed: {
      label: 'Languages served',
      help: 'Distinct guest languages detected.',
    },
    empty: {
      title: 'No metrics yet',
      description: 'Once your receptionist handles calls, the desk fills in with quiet numbers.',
    },
  },
  activity: {
    title: 'Recent activity',
    viewAll: 'View all calls',
    empty: {
      title: 'Nothing to report',
      description: 'Recent calls will appear here as they come in.',
    },
    error: {
      title: 'Could not load activity',
      description: 'Something interrupted the recent activity. Please try again.',
    },
    loading: 'Loading activity',
  },
  status: {
    created: 'Created',
    ringing: 'Ringing',
    answered: 'Answered',
    ai_active: 'AI active',
    handoff_requested: 'Handoff requested',
    human_active: 'Staff on call',
    completed: 'Completed',
    failed: 'Failed',
    unknown: 'Unknown',
  },
  common: {
    notAvailable: '—',
  },
} as const;

export const i18n = {
  namespace: 'reception',
  resources: {
    en,
    bg: en,
    de: en,
    fr: en,
  },
} as const;

export default i18n;
